/**
 * The deadline, as one component both views render.
 *
 * A submission deadline is the single fact this product exists to surface, and it was previously
 * spelled twice — a sentence on the card, and nothing anywhere else. Two renderings of one fact
 * drift: the card said "נותרו 3 ימים" while a table would have said "05/09", and neither told the
 * reader which of those was urgent.
 *
 * So the urgency is DERIVED here, once, from the same day boundaries the server buckets its facet
 * counts with. A posting that lands in the "עד שבוע" facet is painted with the "עד שבוע" colour;
 * anything else would put the sidebar and the row in visible disagreement about the same posting.
 */

import type { ReactNode } from 'react';

/** Israeli date, explicit locale — the browser's default would follow the OS, not the product. */
const DATE_FORMAT = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
/** No year in a table cell: every row would repeat it, and the column is narrow. */
const SHORT_FORMAT = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit' });

export function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : DATE_FORMAT.format(at);
}

export function formatShort(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : SHORT_FORMAT.format(at);
}

/**
 * Days until a deadline, or null when there is none.
 *
 * "Closing soon" is derived HERE and is deliberately not a stored status: the ETL computes `closed`
 * against the clock at pipeline-run time, so anything finer-grained would be stale between runs.
 * Keeping it in the UI makes urgency a presentation choice rather than baked-in data.
 */
export function daysUntil(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.ceil((at - now) / 86_400_000);
}

export type PressureStep = 'now' | 'near' | 'mid' | 'far' | 'none';

/**
 * Days remaining → one of the five pressure steps.
 *
 * THE BOUNDARIES ARE THE SERVER'S. `closingBounds()` in huaca/src/products/tenders/query.ts cuts
 * the deadline facet at 2 / 7 / 30 days, and those cuts are what the sidebar counts and labels. A
 * second opinion here — 3 days instead of 2, say — would colour a row as "עד יומיים" while the
 * facet it belongs to says otherwise, which reads as a bug in the filter rather than in the paint.
 *
 * A deadline already past still lands in `now`, matching the server's `$lte` on the soonest bound.
 */
export function pressureStep(days: number | null): PressureStep {
  if (days === null) return 'none';
  if (days <= 2) return 'now';
  if (days <= 7) return 'near';
  if (days <= 30) return 'mid';
  return 'far';
}

export interface DeadlineProps {
  iso: string | null;
  /**
   * `block` stacks for a card; `cell` runs on one line so it sets a table row's height rather than
   * doubling it. Same markup, same classes, same colours — only the axis differs, in CSS.
   */
  variant?: 'block' | 'cell';
  /** Frozen in tests; the component is otherwise a pure function of the clock. */
  now?: number;
}

export function Deadline({ iso, variant = 'block', now }: DeadlineProps): ReactNode {
  const days = daysUntil(iso, now);
  const step = pressureStep(days);
  const className = ['tenders-due', variant === 'cell' ? 'tenders-due--cell' : '', `tenders-p-${step}`]
    .filter(Boolean)
    .join(' ');

  // No deadline at all. Stated plainly rather than left blank: an empty slot reads as a rendering
  // fault, and these postings are sorted to the end of the deadline order precisely because they
  // have none — the sentinel that puts them there must never reach a reader as the year 9999.
  if (days === null) {
    return (
      <div className={`${className} tenders-due--none`}>
        <span className="tenders-due__lead">ללא מועד</span>
        <span className="tenders-due__note">טרם נקבע</span>
      </div>
    );
  }

  const date = variant === 'cell' ? formatShort(iso) : formatDay(iso);

  // Past due while still listed as open — the pipeline closes postings at run time, so between runs
  // this is a real state. "נותרו -3 ימים" is what the arithmetic would otherwise print.
  if (days < 0) {
    return (
      <div className={`${className} tenders-due--passed`}>
        <span className="tenders-due__lead">המועד חלף</span>
        {date && <span className="tenders-due__date">{date}</span>}
      </div>
    );
  }

  if (days === 0) {
    return (
      <div className={`${className} tenders-due--today`}>
        <span className="tenders-due__lead">נסגר היום</span>
        {date && <span className="tenders-due__date">{date}</span>}
      </div>
    );
  }

  return (
    <div className={className}>
      <span className="tenders-due__top">
        <span className="tenders-due__n">{days}</span>
        <span className="tenders-due__u">{days === 1 ? 'יום' : 'ימים'}</span>
      </span>
      {date && <span className="tenders-due__date">{date}</span>}
    </div>
  );
}
